import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBIcon } from 'mdb-react-ui-kit'
import { UIInputToggleSwitch } from './../../forms/form-fields'
import UserProfileService from './../../../shared/services/UserProfile.service'
import PushNotificationsStore from './../PushNotifications.store'
import PushSelfTest from './../PushSelfTest'
import {
  IOS_INSTALL_STEPS,
  IOS_USE_SAFARI_NOTE,
  getUnblockSteps,
  getOsUnblockSteps,
  describeDeviceStatus,
} from './../pushCopy'

import './PushSettingsPanel.component.scss'

/**
 * The permanent notification control, on /account-alerts.
 *
 * Browser notification permission is granted per browser profile per device,
 * not per account — so every string here says "this device", and the list below
 * shows the agent everywhere they are currently signed up.
 */

/**
 * The API serialises DATETIMEs as ISO-8601 strings, so `new Date(value)` is
 * correct. The fallback only handles a bare "YYYY-MM-DD HH:MM:SS" shape and
 * deliberately does not append a "Z" — asserting a timezone we have not
 * verified would silently shift every timestamp by the UTC offset.
 * Rendered with `undefined` locale = the VIEWER's locale and timezone.
 */
const formatSeen = value => {
  if (!value) return 'Never'
  let d = new Date(value)
  if (isNaN(d.getTime())) d = new Date(`${value}`.replace(' ', 'T'))
  if (isNaN(d.getTime())) return `${value}`
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const Steps = ({ steps }) => (
  <ol className='push-steps'>
    {steps.map((step, i) => (
      <li key={`step-${i}`}>{step}</li>
    ))}
  </ol>
)

const PushSettingsPanel = () => {
  const Store = PushNotificationsStore

  // Reference the imported singleton rather than the local alias so the
  // exhaustive-deps rule can see the dependency is module-stable.
  useEffect(() => {
    if (PushNotificationsStore.isReady) PushNotificationsStore.loadDevices()
  }, [Store.isReady, Store.status])

  const renderThisDevice = () => {
    // While an admin is impersonating an agent, the controls below would act on
    // the ADMIN's browser but read as if they belonged to the agent. Push is
    // never enrolled during impersonation, so show the agent's devices
    // read-only rather than a control that quietly does nothing.
    if (UserProfileService.isAssumed())
      return (
        <p className='push-note push-note--muted'>
          You are viewing this agent's account. Notification settings can only be changed by the agent, on their own
          device.
        </p>
      )

    // Until sync() resolves, status is 'unknown' and the toggle below would
    // render "off" — briefly showing "turn on notifications" to someone who
    // already has them on. Say nothing rather than something wrong.
    if (!Store.isReady) return <p className='push-note push-note--muted'>Checking this device…</p>

    switch (Store.status) {
      case 'disabled':
        return (
          <p className='push-note'>
            Notifications are temporarily switched off for everyone while we work on them. Your messages will still
            appear on this page.
          </p>
        )

      case 'unsupported':
        return (
          <p className='push-note'>
            This browser cannot receive notifications. You will still see every message on this Alerts page.
          </p>
        )

      case 'ios-needs-install':
        return (
          <>
            <p className='push-note'>
              On iPhone and iPad, notifications only work once USABG is saved to your Home Screen.
            </p>
            {Store.browserName !== 'Safari' ? <p className='push-note'>{IOS_USE_SAFARI_NOTE}</p> : null}
            <Steps steps={IOS_INSTALL_STEPS} />
          </>
        )

      case 'denied':
        return (
          <>
            <p className='push-note'>
              Notifications are blocked for this site. Your browser will not ask again, so this has to be changed in its
              settings:
            </p>
            <Steps steps={getUnblockSteps(Store.browserName, Store.platform)} />
          </>
        )

      case 'error':
        return (
          <p className='push-note push-note--error' role='alert'>
            {Store.error || 'Notifications could not be set up on this device.'}
          </p>
        )

      default:
        return (
          <div className='push-toggle-row'>
            <UIInputToggleSwitch
              id='push-this-device'
              name='push_this_device'
              value={Store.isEnabled}
              disabled={Store.isBusy}
              labelOn='On'
              labelOff='Off'
              onChange={() => (Store.isEnabled ? Store.disable() : Store.enable())}
            />
            <span className='push-toggle-label'>
              {Store.isEnabled
                ? `Notifications are on for this device (${Store.deviceLabel}).`
                : `Turn on notifications for this device (${Store.deviceLabel}).`}
            </span>
          </div>
        )
    }
  }

  const devices = Store.devices || []

  return (
    <section id='PushSettingsPanelComponent'>
      <header>
        <h4>
          <MDBIcon icon='bell' /> Notifications
        </h4>
        <p className='push-intro'>
          Get alerted about time-sensitive items even when the back office is closed. Notifications are set per device,
          so turn them on anywhere you use USABG.
        </p>
      </header>

      <div className='push-this-device'>{renderThisDevice()}</div>

      {/* "Did you see that?" — catches OS-level suppression, which no browser
          API can detect. Renders nothing unless a test is awaiting an answer. */}
      <PushSelfTest />

      {/* The self-test's answer lives in memory and resets on every load, so
          without this the warning disappeared on the next refresh and the panel
          above went back to reporting "Notifications are on for this device" —
          to the one person who had already told us they were not appearing.
          Driven off the server row, so it survives reloads and cleared storage.
          Suppressed while PushSelfTest is on screen saying the same thing. */}
      {Store.isSilentlySuppressed && Store.selfTest !== 'pending' && Store.selfTest !== 'failed' ? (
        <div className='push-os-blocked' role='alert'>
          <p className='push-note push-note--error'>
            <MDBIcon icon='exclamation-triangle' /> Notifications are on for USABG, but you told us they are not
            appearing on this device. That is a setting on your computer or phone rather than in USABG:
          </p>
          <Steps steps={getOsUnblockSteps(Store.platform)} />
        </div>
      ) : null}

      {Store.isEnabled ? (
        <button type='button' className='push-retest' onClick={() => Store.runSelfTest()}>
          Send a test notification to this device
        </button>
      ) : null}

      {devices.length > 0 && (
        <div className='push-device-list'>
          <h5>Your devices</h5>
          <ul>
            {devices.map(device => {
              const isCurrent = Store.isCurrentDevice(device)
              return (
                <li key={`device-${device.id}`} className={isCurrent ? 'is-current' : ''}>
                  <div className='push-device-main'>
                    <span className='push-device-name'>
                      {device.label || 'Unknown device'}
                      {isCurrent ? <em> — this device</em> : null}
                    </span>
                    <span className='push-device-status'>
                      {describeDeviceStatus(device)} · last used {formatSeen(device.last_seen_at)}
                    </span>
                  </div>
                  {!UserProfileService.isAssumed() &&
                    !device.suppressed_at &&
                    !device.revoked_at &&
                    device.permission_state === 'granted' && (
                      <button
                        type='button'
                        className='push-device-remove'
                        disabled={Store.isBusy}
                        onClick={() => Store.removeDevice(device.device_uid)}
                      >
                        Turn off
                      </button>
                    )}
                </li>
              )
            })}
          </ul>
          <p className='push-note push-note--muted'>
            Turning off a device stops notifications there straight away, even if you are not using it right now. To
            turn one back on, open USABG on that device and switch notifications on again.
          </p>
        </div>
      )}
    </section>
  )
}

export default observer(PushSettingsPanel)
